/**
 * Le registre d'affluences (CCT_01 § 3.1.4, PA-05) — une ligne par patient
 * entré dans un site. L'accueil l'écrit à l'entrée, une fois l'éligibilité
 * lue ; la Direction le compte.
 *
 * Les colonnes reprennent le registre papier d'AFS : centre, nom, genre,
 * type, matricule, société, entité d'affiliation, produit, date, heure,
 * prestation, repos médical / assistance maternelle, observation.
 */
import { z } from 'zod';
import { PARCOURS_POSSIBLES } from '../rules/eligibilite.js';
import { TYPES_BENEFICIAIRE } from '../rules/beneficiaire.js';
import { SEXES } from './personnes.js';
import { ORDRES_DE_TRI, schemaPage, schemaPagination } from './administration.js';
/** Décision 446 — le repos médical, en jours entiers : de 0 (sans repos) à ce plafond. */
export const REPOS_MEDICAL_MAX_JOURS = 365;
/**
 * Les spécialités qu'un centre peut doter d'un box, telles que le registre
 * les nomme. Décisions 171 et 174 (21-22/10) : ni analyses (partenaires
 * PEC), ni pharmacie (AFS ne vend pas de médicaments) ; l'ophtalmologie y
 * revient avec la décision 338 (tarif externe et étranger, 29/10). Ce
 * qu'un centre propose à l'entrée et à la caisse, c'est la prestation de
 * ses box (`GET /sites/courant/prestations`), pas toute cette liste. Une
 * visite ou un reçu déjà enregistré garde sa prestation, quelle qu'elle soit.
 */
export const PRESTATIONS_COURANTES = [
    'GÉNÉRALISTE',
    'DENTISTE',
    'GYNÉCOLOGIE',
    'ÉCHOGRAPHIE',
    'VISITE SYSTÉMATIQUE',
    'VISITE D’EMBAUCHE',
    'MÉDECINE DU TRAVAIL',
    'CARDIOLOGIE',
    'ENDOCRINOLOGIE',
    'CPN',
    'KINÉSITHÉRAPIE',
    'SOINS',
    'OPHTALMOLOGIE',
];
/**
 * Décision 174 — le refus d'une entrée ou d'un encaissement pour une
 * prestation qu'aucun box en service du centre ne reçoit (422).
 */
export const REGLE_PRESTATION_HORS_CENTRE = 'PRESTATION_HORS_CENTRE';
/**
 * Une prestation du centre : celle d'un de ses box en service. `tarif` :
 * une ligne active de la grille la rend payable à la caisse ; sans elle,
 * la caisse la montre grisée (« voir la Direction »).
 */
export const schemaPrestationDuCentre = z.object({
    prestation: z.string(),
    tarif: z.boolean(),
});
/** La colonne TYPE du registre papier : AGENT, CONJOINT, ENFANT, EXTERNE. */
export const LIBELLE_TYPE_REGISTRE = {
    TITULAIRE: 'Agent',
    CONJOINT: 'Conjoint',
    ENFANT: 'Enfant',
    EXTERNE: 'Externe',
};
export const PRIORITES_VISITE = ['NORMALE', 'URGENCE', 'PRIORITE_MEDICALE'];
export const STATUTS_VISITE = [
    'EN_ATTENTE',
    'EN_CONSULTATION',
    'TERMINEE',
    'ABSENT',
    'ANNULEE',
];
/** L'issue d'un passage, dans les mots du registre — l'écran et l'export de l'entreprise. */
export const LIBELLE_STATUT_VISITE = {
    EN_ATTENTE: 'En attente',
    EN_CONSULTATION: 'En consultation',
    TERMINEE: 'Reçu',
    ABSENT: 'Absent à l’appel',
    ANNULEE: 'Annulée',
};
export const schemaEnregistrementVisite = z
    .object({
    /**
     * Ce que le patient vient chercher. Si l'agence a des box de cette
     * prestation, il attend dans la file commune de cette prestation et le
     * box se fixe à l'appel. Une prestation qu'aucun box en service du
     * centre ne reçoit est refusée (décision 174, ressaisie comprise).
     */
    prestation: z
        .string()
        .trim()
        .min(1, 'Dites quelle prestation le patient vient chercher.')
        .max(80),
    reposMedicalJours: z.number().int().min(0).max(REPOS_MEDICAL_MAX_JOURS).optional().nullable(),
    assistanceMaternelle: z.boolean().optional(),
    observation: z.string().trim().max(300).optional().nullable(),
    /** RG-PA-10 — une urgence passe devant, et le dit. */
    priorite: z.enum(PRIORITES_VISITE).optional(),
    motifPriorite: z.string().trim().max(200).optional().nullable(),
    /** Caisse (spécification du 16/10, R1) : le reçu d'un patient non couvert. */
    encaissementId: z.uuid().optional(),
    /**
     * Mode dégradé (CP-12) — l'heure notée sur le formulaire papier pendant une
     * coupure. Présente : c'est une RESSAISIE, l'entrée prend cette heure et le
     * registre dit qu'elle a été saisie après coup. Elle ne passe par aucun box :
     * le patient a déjà été reçu.
     */
    survenueLe: z.iso.datetime().optional(),
    /**
     * R5 — le praticien que le patient demande : il doit tenir en ce moment
     * un box de la prestation, dans ce centre. Refusé si la Direction n'a pas
     * activé `file.praticien_demande`.
     */
    praticienDemandeId: z.uuid().nullable().optional(),
    /**
     * Essai G19 (21/10) — le patient a déjà été servi ce jour-là pour cette
     * prestation : l'accueil le fait entrer quand même, sous sa
     * responsabilité. La valeur est l'identifiant du passage déjà servi que
     * l'écran a montré (`details.visiteId` du refus) : une confirmation ne
     * couvre jamais un passage qu'on n'a pas vu.
     */
    confirmeDejaServi: z.uuid().optional(),
})
    .strict();
/**
 * Essai G19 (21/10) — le refus d'une entrée (ou d'un encaissement) pour un
 * patient déjà servi ce jour-là pour la même prestation. Il se lève en
 * renvoyant la demande avec `confirmeDejaServi`.
 */
export const RAISON_DEJA_SERVI = 'DEJA_SERVI_LE_MEME_JOUR';
/** Ce que le refus dit du passage déjà servi : de quoi alerter, rien de médical. */
export const schemaDetailsDejaServi = z.object({
    visiteId: z.uuid(),
    prestation: z.string(),
    /** Le jour civil à Madagascar, `AAAA-MM-JJ`. */
    jour: z.string(),
    /** L'heure d'entrée au registre, `HH:MM` à Madagascar. */
    heure: z.string(),
    /** L'heure de fin du passage, `HH:MM` ; `null` pour une ressaisie après coupure. */
    termineeA: z.string().nullable(),
    site: z.string(),
});
/**
 * Le ticket de passage (décision 283, R11) : ce qui s'imprime, et rien
 * d'autre — ni nom, ni matricule, ni information médicale.
 */
export const schemaTicketPassage = z.object({
    numero: z.string(),
    prestation: z.string(),
    centre: z.string(),
    arriveeLe: z.iso.datetime(),
});
export const schemaVisite = z.object({
    id: z.uuid(),
    site: z.object({ id: z.uuid(), code: z.string(), nom: z.string() }),
    personne: z.object({
        id: z.uuid(),
        nom: z.string(),
        prenom: z.string().nullable(),
        sexe: z.enum(SEXES).nullable(),
    }),
    arriveeLe: z.iso.datetime(),
    prestation: z.string(),
    parcours: z.enum(PARCOURS_POSSIBLES),
    eligible: z.boolean(),
    motifNonEligible: z.string().nullable(),
    /**
     * Décision 376 — entrée admise sur un « Peut entrer » de la chargée : couverte
     * dossier réactivé ou admis pour la journée ; `motifNonEligible` garde le refus d'origine.
     */
    admisSurVerification: z.boolean(),
    typeBeneficiaire: z.enum(TYPES_BENEFICIAIRE),
    matricule: z.string().nullable(),
    adherentNom: z.string().nullable(),
    entiteCode: z.string().nullable(),
    offreNom: z.string().nullable(),
    reposMedicalJours: z.number().int().nullable(),
    assistanceMaternelle: z.boolean(),
    observation: z.string().nullable(),
    priorite: z.enum(PRIORITES_VISITE),
    motifPriorite: z.string().nullable(),
    statut: z.enum(STATUTS_VISITE),
    box: z.object({ id: z.uuid(), nom: z.string() }).nullable(),
    praticien: z.object({ id: z.uuid(), nom: z.string(), prenom: z.string().nullable() }).nullable(),
    priseEnChargeLe: z.iso.datetime().nullable(),
    /** Le dernier rappel du praticien, quand le patient n'a pas entendu son appel. */
    rappeleLe: z.iso.datetime().nullable(),
    /**
     * AJ-07 — le clic « Commencer la consultation » : `null` sur une visite
     * EN_CONSULTATION, le patient est appelé mais pas encore entré. Vide aussi
     * sur une visite terminée avant AJ-07 : sa consultation part de l'appel.
     */
    consultationCommenceeLe: z.iso.datetime().nullable(),
    termineeLe: z.iso.datetime().nullable(),
    /** Mode dégradé : quand cette entrée, notée sur papier pendant une coupure, a été ressaisie. */
    saisieDiffereeLe: z.iso.datetime().nullable(),
    /** Entrée notée par erreur, annulée tant que le patient attendait : pourquoi. */
    motifAnnulation: z.string().nullable(),
    /** Le numéro de passage (`GA12`), figé à l'entrée ; `null` sans interrupteur ou en ressaisie. */
    numeroPassage: z.string().nullable(),
    /**
     * Décision 338 (5) — suite d'un soin dentiste déjà payé : la visite d'origine.
     * Facultatif : absent des réponses qui ne le lisent pas.
     */
    visiteOrigine: z.object({ id: z.uuid(), arriveeLe: z.iso.datetime() }).nullable().optional(),
    enregistrePar: z.object({
        id: z.uuid(),
        nom: z.string(),
        prenom: z.string().nullable(),
    }),
});
/** Annuler une entrée notée par erreur : le motif est exigé, il reste au registre. */
export const schemaAnnulationVisite = z
    .object({ motif: z.string().trim().min(3).max(500) })
    .strict();
export const TRIS_REGISTRE = ['heure', 'nom', 'societe', 'prestation'];
/** Le registre d'un jour, sur un site ou sur tout le périmètre. */
export const schemaFiltresRegistre = schemaPagination.extend({
    tri: z.enum(TRIS_REGISTRE).default('heure'),
    ordre: z.enum(ORDRES_DE_TRI).default('desc'),
    /** `AAAA-MM-JJ`, le jour courant par défaut (heure du serveur). */
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    siteId: z.uuid().optional(),
});
export const schemaRegistre = schemaPage(schemaVisite).extend({
    date: z.string(),
});
/**
 * Décision 458 — le registre d'un mois pour UNE entreprise, en fichier Excel
 * que la chargée remet à l'entreprise. Tous les centres, sauf filtre.
 */
export const schemaFiltresExportEntreprise = z.object({
    /** `AAAA-MM`, un mois civil de Madagascar. */
    mois: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    adherentId: z.uuid(),
    siteId: z.uuid().optional(),
});
/**
 * Le nom du fichier : l'entreprise et le mois, en ASCII seul — l'en-tête
 * `Content-Disposition` n'accepte pas d'autre caractère sans encodage, et
 * l'écran le reprend pour le téléchargement.
 */
export function nomFichierExportEntreprise(code, nom, mois) {
    const propre = (texte) => texte
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Za-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40);
    return `registre-${propre(code)}-${propre(nom)}-${mois}.xlsx`.replace(/-+/g, '-');
}
/** Les refus annoncés de l'export, que l'écran distingue d'une panne. */
export const REGLE_EXPORT_SANS_PASSAGE = 'EXPORT_SANS_PASSAGE';
export const REGLE_EXPORT_TROP_GROS = 'EXPORT_TROP_GROS';
/** Le tableau de bord des affluences — des comptes, sur une période. */
export const schemaFiltresAffluences = z.object({
    du: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    au: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    siteId: z.uuid().optional(),
});
const compte = z.object({ cle: z.string(), libelle: z.string(), nombre: z.number().int() });
export const schemaAffluences = z.object({
    du: z.string(),
    au: z.string(),
    total: z.number().int(),
    prisEnCharge: z.number().int(),
    paiementImmediat: z.number().int(),
    /** Une entrée par jour de la période, jours vides compris. */
    parJour: z.array(compte),
    parSite: z.array(compte),
    parPrestation: z.array(compte),
    parEntreprise: z.array(compte),
    parOffre: z.array(compte),
    parType: z.array(compte),
});
//# sourceMappingURL=visites.js.map